import React, {useRef, useCallback} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars';
import EditQuestionModal from './EditQuestionModal';
import SurveyTable from './SurveyTable';
import SurveyFilterBar from './SurveyFilterBar';
import SurveyPagePageBar from './SurveyPagePageBar';
import DeleteSurveyQuestionModal from './DeleteSurveyQuestionModal';
import useModal from 'modals/useModal';
import PageLoadingComponent from 'projects/components/PageLoadingComponent';
import useVisibleColumns from './useVisibleColumns';
import GearDropdown from 'projects/components/GearDropdown';
import SurveyButtonToolbar from './SurveyButtonToolbar';
import SurveyNotFound from './SurveyNotFound';
import { useSurveyQuestions, useDeleteSurveyQuestion, useSurveyRows } from 'hooks/surveys/queries';

import {
  goNext,
  goPrevious,
  editQuestionUrl,
  hideUrl,
  optionClicked,
  deleteFilter,
  clearCheckedUrl,
  optionRemovedUrl,
  getFilters,
  questionAddedUrl,
  optionAddedUrl
} from 'utils/surveyUrlEncoder';

import {
  getBase64Filter,
  getParams
} from 'utils/urlEncoder';

const SurveyPage = ({ hasEditPermission }) => {
  const history = useHistory();
  const location = useLocation();
  const { surveyID, surveyQuestionID: surveyQuestionIDParam } = useParams();
  const filterRef = useRef(null);
  const surveyId = surveyID;

  // Get survey questions using react-query hook
  const {
    data: surveyQuestionsData,
    isLoading: loadingQuestions,
    isSuccess: questionsLoaded,
  } = useSurveyQuestions(surveyId);

  const surveyQuestions = surveyQuestionsData?.survey_questions || [];

  // Get survey rows using react-query hook with filter
  const filter = getBase64Filter(location);
  const {
    data: surveyRowsData,
    isLoading: loadingRows,
    isError: isErrorRows,
    error: rowsError
  } = useSurveyRows(surveyId, filter);

  const surveyRows = surveyRowsData?.surveyRows || [];
  const pageNext = surveyRowsData?.pageNext !== undefined ? surveyRowsData.pageNext : null;

  // Determine if we have a not found error
  const notFound = isErrorRows && (rowsError?.status === 404 || rowsError?.status === 403);

  const {
    visibleColumns,
    toggleColumnVisibility,
    resetColumns,
  } = useVisibleColumns(surveyId, surveyQuestions, questionsLoaded);
  
  // React Query mutation for deleting survey questions
  const deleteQuestionMutation = useDeleteSurveyQuestion({
    surveyId,
    onError: (error) => {
      console.error('Failed to delete question:', error);
      // You could add error handling here, like showing a toast notification
    }
  });

  const deleteQuestion = useCallback((question_id) => {
    deleteQuestionMutation.mutate(question_id);
  }, [deleteQuestionMutation]);

  const {
    showModal,
    onShowModal,
    onCancelModal,
    onConfirmModal
  } = useModal(deleteQuestion);

  const surveyQuestionID = Number(surveyQuestionIDParam); // Convert to number
  const editQuestion = surveyQuestions.find((question) => question.id === surveyQuestionID);  

  // onHide for the modal should just go to the base survey page
  const onHide = () => {
    history.push(hideUrl(location));
  }

  const onQuestionClicked = (question_id) => {
    history.push(`/survey_questions/${question_id}`);
  }

  const onEditQuestion = (question_id) => {
    history.push(
      editQuestionUrl(location, question_id)
    )
  }

  const onGoNext = () => {
    history.push(goNext(location, pageNext));
  }

  const onGoPrevious = () => {
    history.push(goPrevious(location, 20));
  }

  const onOptionClicked = (question_id, option_id) => {
    history.push(optionClicked(location, question_id, option_id))
  }

  const onQuestionAdded = (question_id) => {
    history.push(questionAddedUrl(location, question_id));
  }

  const onOptionAdded = (question_id, option_id) => {
    history.push(optionAddedUrl(location, question_id, option_id));
  }

  const onOptionRemoved = (question_id, option_id) => {
    history.push(optionRemovedUrl(location, question_id, option_id));
  }

  const onDeleteFilter = (question_id) => {
    history.push(deleteFilter(
      location,
      question_id
    ))
  }

  const onClearChecked = (question_id) => {
    history.push(clearCheckedUrl(location, question_id));
  }

  const filterHeight = filterRef.current ? filterRef.current.clientHeight : 0;
  const filters = getFilters(location);
  const padding = 25;

  if (notFound) {
    return <SurveyNotFound />;
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <EditQuestionModal
        question_type={editQuestion?.question_type}
        question_id={editQuestion?.id}
        surveyId={surveyId}
        onHide={onHide}
        hasEditPermission={hasEditPermission}
      />

      <DeleteSurveyQuestionModal
        cancelDelete={onCancelModal}
        confirmDelete={onConfirmModal}
        showModal={showModal}
      />

      <div ref={filterRef} style={{ display: 'flex', alignItems: 'stretch', paddingLeft: `${padding - 10}px` }}>
        {!loadingQuestions && 
        <>
          <div style={{ flex: 1 }}>
            <SurveyFilterBar
              surveyQuestions={surveyQuestions}
              filters={filters}
              questionAdded={onQuestionAdded}
              optionAdded={onOptionAdded}
              onOptionRemoved={onOptionRemoved}
              onDeleteFilter={onDeleteFilter}
              onClearChecked={onClearChecked}
            />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end' }}>
           <SurveyButtonToolbar> {/* Unfortunately this is needed to get the modal to line up correctly*/}
              <GearDropdown
                codes={surveyQuestions.map(question => ({
                  name: question.question_text,
                  id: question.id,
                  checked: visibleColumns.some(column => column.id === question.id)
                }))}
                onChecked={(codeID, checked) => toggleColumnVisibility(codeID)}
                subHeader={"Select columns to show"}
              />
            </SurveyButtonToolbar>
          </div>
        </>
        }
      </div>

      <div style={{flex: 1, overflow: "hidden"}}>
        <Scrollbars autoHide style={{height: "100%"}}>
        <div style={{paddingLeft: padding}}>
          {!loadingQuestions && <SurveyTable
            surveyQuestions={visibleColumns}
            surveyRows={surveyRows}
            onOptionClicked={onOptionClicked}
            onEditQuestion={onEditQuestion}
            onQuestionClicked={onQuestionClicked}
            onDeleteQuestion={onShowModal}
            loading={loadingRows}
            toggleColumnVisibility={toggleColumnVisibility}
            hasEditPermission={hasEditPermission}
          />}
          {!loadingRows && !loadingQuestions && <SurveyPagePageBar
            onGoPrevious={onGoPrevious}
            onGoNext={onGoNext}
            pageNext={pageNext}
            offset={getParams(location).offset}
            disabled={loadingRows}/>}
          {
            (loadingQuestions || loadingRows) && <div style={{marginTop: "20px"}}>
                <PageLoadingComponent />
              </div>
          }
        </div>
        </Scrollbars>
      </div>
    </div>
  );
};

export default SurveyPage;